// apps/roleforge-app/src/components/invitations/StakeholderInviteList.tsx
// Plan 3b-ii Phase 8.5: List of stakeholder invitations with status, copy-link, revoke.
//
// Issue #1204: "Delete" was a single unconfirmed click that hard-deleted the
// invitation row along with every response that stakeholder had contributed.
// It is now "Revoke", it asks for confirmation inline (keyboard-reachable, no
// browser dialog), and the server soft-deletes so the responses survive.
//
// Issue #1374: after #1304 hashed invite tokens at rest, a row the page did not
// just create had NO link affordance at all — no button, no explanation. Every
// such row now says plainly that the link cannot be shown and offers Resend,
// which rotates the token server-side and hands back a fresh URL. Because
// rotation retires the link already in the stakeholder's inbox, the warning is
// shown BEFORE the request goes out, not after.

import { useState } from 'react';
import type { InvitationRow } from '../../lib/api-client';

/**
 * Issue #1374 — the invalidation warning. Kept as a single exported constant so
 * the confirmation copy and the control's tooltip cannot drift apart, and so a
 * test can assert on the real string the owner reads.
 */
export const RESEND_INVALIDATION_WARNING =
  'Resending issues a new link and breaks the one already sent — a stakeholder still holding the old link will get an error.';

interface Props {
  invitations: InvitationRow[];
  /**
   * Issue #1304: invite links are now stored as `sha256(token)`, so the server
   * cannot hand one back on a list read — the URL exists only in the recipient's
   * email and, for the length of this page visit, in whatever the create call
   * just returned. This map is keyed by invitation id and holds only those
   * just-created links; "Copy link" shows for those rows and nowhere else.
   *
   * Issue #1374: a successful Resend adds its rotated URL here too, so the
   * freshly-rotated link is surfaced exactly the way a newly-created one is.
   */
  respondUrls?: Record<string, string>;
  /** Revokes the invite link. The server retains the row and its responses. */
  onDelete: (id: string) => void;
  /**
   * Issue #1374: re-mails the invitation and rotates its token. The parent owns
   * the request so the rotated URL lands in `respondUrls` alongside create's.
   */
  onResend: (id: string) => void;
  /** Invitation id with a resend in flight, if any. */
  resendingId?: string | null;
  /**
   * Per-row resend failure text, straight from the server. Issue #1361 records
   * `TaskDetailPanel` swallowing errors in a bare `catch {}`; a resend that
   * silently does nothing is the same defect, so the message is rendered.
   */
  resendErrors?: Record<string, string>;
}

function statusBadge(invitation: InvitationRow): { label: string; className: string } {
  if (invitation.completed_at) {
    return {
      label: 'Completed',
      className: 'bg-mint/10 text-mint',
    };
  }
  if (invitation.accepted_at) {
    return {
      label: 'Accepted',
      className: 'bg-coral/10 text-coral',
    };
  }
  return {
    label: 'Invited',
    className: 'bg-white/10 text-ink',
  };
}

export function StakeholderInviteList({
  invitations,
  respondUrls,
  onDelete,
  onResend,
  resendingId,
  resendErrors,
}: Props) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmingResendId, setConfirmingResendId] = useState<string | null>(null);

  if (invitations.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-line bg-card px-4 py-6 text-sm text-graphite">
        No invitations sent yet.
      </div>
    );
  }

  const handleCopyLink = async (invitation: InvitationRow) => {
    const link = respondUrls?.[invitation.id];
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(invitation.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback: prompt with the link
      window.prompt('Copy this link:', link);
    }
  };

  return (
    <ul className="flex flex-col gap-3">
      {invitations.map((inv) => {
        const badge = statusBadge(inv);
        const confirming = confirmingId === inv.id;
        const hasContributed = Boolean(inv.completed_at ?? inv.accepted_at);
        const link = respondUrls?.[inv.id];
        // A completed interview has consumed its link: #1304's bounded-reuse
        // guard answers `token_consumed` (403) once `completed_at` is set, and
        // `reissue()` does not clear that stamp. So Resend on a completed row
        // would mail a link that is guaranteed to fail — the exact dead-link
        // outcome this change exists to prevent. Say so instead of offering it.
        const linkConsumed = Boolean(inv.completed_at);
        const canResend = !linkConsumed;
        const confirmingResend = confirmingResendId === inv.id;
        const resending = resendingId === inv.id;
        const resendError = resendErrors?.[inv.id];
        return (
          <li
            key={inv.id}
            className="flex flex-col gap-3 rounded-lg border border-line bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-sm font-medium text-ink truncate">
                {inv.recipient_email}
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                >
                  {badge.label}
                </span>
                {inv.role_hint && (
                  <span className="text-xs text-graphite">{inv.role_hint}</span>
                )}
              </div>
              {/*
                Issue #1374: make the link state legible on every row. Before
                this, a row without a held URL simply had no button and no
                explanation, which read as a broken page rather than as a
                deliberate consequence of hashing tokens at rest.
              */}
              {!link && (
                <p className="text-xs leading-5 text-graphite" data-testid={`invite-link-state-${inv.id}`}>
                  {linkConsumed
                    ? 'Interview complete — this link is used up and no longer opens the audit.'
                    : 'Invite link can’t be shown: we store only a hashed copy, so it exists in their email and nowhere else. Resend to issue a new one.'}
                </p>
              )}
              {resendError && (
                <p
                  className="text-xs leading-5 text-ember"
                  role="alert"
                  data-testid={`resend-error-${inv.id}`}
                >
                  Resend failed: {resendError}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {link && (
                <button
                  type="button"
                  onClick={() => handleCopyLink(inv)}
                  className="rounded-md px-2 py-1 text-xs font-semibold text-coral transition hover:bg-coral/10"
                  title="Copy invite link — available until you leave this page"
                >
                  {copiedId === inv.id ? 'Copied!' : 'Copy link'}
                </button>
              )}
              {canResend &&
                (confirmingResend ? (
                  <div
                    className="flex flex-col items-start gap-2 sm:flex-row sm:items-center"
                    role="group"
                    aria-label={`Confirm resending the invitation for ${inv.recipient_email}`}
                    data-testid={`resend-confirm-${inv.id}`}
                  >
                    <span className="text-xs leading-5 text-graphite">
                      {RESEND_INVALIDATION_WARNING}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmingResendId(null);
                          onResend(inv.id);
                        }}
                        className="rounded-md bg-coral/10 px-2 py-1 text-xs font-semibold text-coral transition hover:bg-coral/20"
                        data-testid={`resend-confirm-yes-${inv.id}`}
                      >
                        Yes, resend
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingResendId(null)}
                        className="rounded-md px-2 py-1 text-xs font-semibold text-graphite transition hover:bg-white/10"
                        data-testid={`resend-confirm-cancel-${inv.id}`}
                      >
                        Keep current link
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingResendId(inv.id)}
                    disabled={resending}
                    className="rounded-md px-2 py-1 text-xs font-semibold text-coral transition hover:bg-coral/10 disabled:opacity-60"
                    // The warning is on the control itself as well as in the
                    // confirmation, so it is readable without committing to
                    // anything.
                    title={RESEND_INVALIDATION_WARNING}
                    aria-label={`Resend the invitation for ${inv.recipient_email}. ${RESEND_INVALIDATION_WARNING}`}
                    data-testid={`resend-${inv.id}`}
                  >
                    {resending ? 'Resending…' : 'Resend'}
                  </button>
                ))}
              {confirming ? (
                <div
                  className="flex items-center gap-2"
                  role="group"
                  aria-label={`Confirm revoking the invitation for ${inv.recipient_email}`}
                  data-testid={`revoke-confirm-${inv.id}`}
                >
                  <span className="text-xs text-graphite">
                    {hasContributed
                      ? 'Revoke link? Their answers stay in the audit.'
                      : 'Revoke link?'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmingId(null);
                      onDelete(inv.id);
                    }}
                    className="rounded-md bg-ember/10 px-2 py-1 text-xs font-semibold text-ember transition hover:bg-ember/20"
                    data-testid={`revoke-confirm-yes-${inv.id}`}
                  >
                    Yes, revoke
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingId(null)}
                    className="rounded-md px-2 py-1 text-xs font-semibold text-graphite transition hover:bg-white/10"
                    data-testid={`revoke-confirm-cancel-${inv.id}`}
                  >
                    Keep it
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingId(inv.id)}
                  className="rounded-md px-2 py-1 text-xs font-semibold text-ember transition hover:bg-ember/10"
                  title="Revoke this invite link"
                  aria-label={`Revoke the invitation for ${inv.recipient_email}`}
                  data-testid={`revoke-${inv.id}`}
                >
                  Revoke
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
